import React, { useEffect, useState } from 'react';

import getHiredImage from 'images/get-hired.svg';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { OnboardingTitle } from 'registration/containers/Onboarding';

import { updateTalentProfile } from 'shared/actions/talentProfileActions';
import { Checkbox, LABEL_PLACEMENT } from 'shared/components/ihcl/checkbox';
import { Stack } from 'shared/components/ihcl/patterns';
import { styled } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import activateABTest from 'shared/helpers/activateABTest';
import { trackClick } from 'shared/helpers/tracking';

import { useOnboardingStepContext } from '../providers/OnboardingStepProvider';
import { useOnboardingVariantContext } from '../providers/OnboardingVariantProvider';
import StickySubmitFooter from './StickySubmitFooter';

type GetHiredProps = {
  dispatch: ThunkDispatch<any, undefined, any>;
  profileType?: 'nurse' | 'tech';
};

const NURSE_SALARY_RANGE = '$330k';
const TECH_SALARY_RANGE = '$200k';

const CheckboxWrapper = styled('div', {
  marginTop: '16px',
  paddingLeft: '48px',
  paddingRight: '48px',
});

const GetHired = ({ dispatch, profileType = 'nurse' }: GetHiredProps) => {
  const {
    addTalentProfileKeys,
    completeStep,
    localStepTalentProfile,
    isUpdatingProfile,
  } = useOnboardingStepContext();
  const { currentStep, intent } = useOnboardingVariantContext();
  const history = useHistory();

  const [optedIn, setOptedIn] = useState(true);

  useEffect(() => {
    addTalentProfileKeys(['currentOnboardStep', 'id']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    trackClick(
      optedIn
        ? 'talent.hiring_activation_click'
        : 'talent.hiring_activation_skip',
      { intent }
    );
    // Update the current onboard step back to 2 if we are now going to
    // go through the hiring flow (product wants to skip step 1)
    const variantGroup =
      profileType === 'tech' ? 'techOnboarding' : 'onboarding';
    const legacyCalculation = profileType !== 'tech';

    const onboardVariant = optedIn
      ? activateABTest(variantGroup, { legacyCalculation })
      : localStepTalentProfile.onboard_variant;
    const profileAttributesWithStep = {
      current_onboard_step: optedIn ? 2 : currentStep,
      onboard_variant: onboardVariant,
    };
    dispatch(
      updateTalentProfile(
        localStepTalentProfile.id,
        profileAttributesWithStep,
        () => {
          if (optedIn) {
            if (profileType === 'tech') {
              history.push('/tech_onboard', {
                isLiteOnboardingFlow: true,
              });
            } else {
              history.push('/talent_onboard', {
                isLiteOnboardingFlow: true,
              });
            }
          } else {
            completeStep({});
          }
        }
      )
    );
  };

  return (
    <>
      <div>
        <Stack $gapSizing="unit16" $alignItems="center">
          <img alt="" src={getHiredImage} style={{ paddingTop: '40px' }} />

          <OnboardingTitle $noBottomMargin $noTopMargin>
            Interested in permanent{' '}
            {profileType === 'tech' ? 'tech' : 'nursing'} job opportunities?
          </OnboardingTitle>

          <Text variant="LabelMedium" color="grayBodyCopy">
            We make it easy to get matched to positions in our network of 1500+
            healthcare employers, with salaries up to{' '}
            {profileType === 'tech' ? TECH_SALARY_RANGE : NURSE_SALARY_RANGE}.
          </Text>
          <Text variant="LabelMedium" color="grayBodyCopy">
            It only takes a couple minutes to set up your profile.
          </Text>

          <CheckboxWrapper>
            <Checkbox
              checked={optedIn}
              onChange={() => setOptedIn(!optedIn)}
              labelPlacement={LABEL_PLACEMENT.right}
              labelWeight="bold"
            >
              Yes, I&apos;m open to permanent job opportunities!
            </Checkbox>
          </CheckboxWrapper>
        </Stack>
      </div>

      <StickySubmitFooter disabled={isUpdatingProfile} onClick={onClick} />
    </>
  );
};

export default connect()(GetHired);
